<template>
  <div class="presentation-detail">
    <div class="presentation-detail-header">
      <div class="back-to-list" @click="backToAttendedPresentationList">
        <span class="icon">
          <i class="fas fa-chevron-left" />
        </span>
        <span class="text-support">Back to presentation list</span>
        <span class="text">{{ $t("presentation_details") }}</span>
      </div>
    </div>
    <div class="presentation-detail-body">
      <div v-if="isLoading" class="presentation-detail-loading">
        <SkeletonLoadingBars v-for="index in 1" :key="index" />
      </div>
      <PresentationLookback
        v-else
        :presentation="detail.presentation"
        :audienceId="detail.audienceId"
        :activeTabView="'profile'"
        :slides="slides"
        :slide="currentSlide"
        :slideIndex="currentSlideIndex"
        :maxLookbackSlideIndex="currentMaxLookbackSlideIndex"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import util from '@/util'
import * as Sentry from '@sentry/vue'
import SlideApi from '@/api/slide'
import PresentationLookback from '@/components/my-quiz-answers/PresentationLookback.vue'
import AttendedPresentationsApi from '@/api/attended-presentations'
import SkeletonLoadingBars from '@/components/my-quiz-answers/SkeletonLoadingBars.vue'

export default {
  components: {
    PresentationLookback,
    SkeletonLoadingBars,
  },
  data() {
    return {
      detail: {},
      isLoading: true,
      slides: [],
    }
  },
  computed: {
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapState({
      slideIndex: (state) => state.slideIndex,
      maxLookbackSlideIndex: (state) => state.maxLookbackSlideIndex,
    }),
    isInSession() {
      const uniqueAccessCode = this.detail
        && this.detail.presentation
        && this.detail.presentation.uniqueAccessCode

      return this.$route.params.id && this.$route.params.id === uniqueAccessCode
    },
    currentSlide() {
      return this.isInSession
        ? this.slide
        : ((this.slides && this.slides.length && this.slides[this.slides.length - 1]) || {})
    },
    currentSlideIndex() {
      return this.isInSession
        ? this.slideIndex
        : (this.slides && this.slides.length - 1)
    },
    currentMaxLookbackSlideIndex() {
      return this.isInSession
        ? this.maxLookbackSlideIndex
        : (this.slides && this.slides.length - 1)
    },
  },
  watch: {
    detail: {
      handler(value) {
        if (value && value.presentationId) {
          this.getDataSlides(value.presentationId)
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.getAttendedPresentationDetail()
  },
  methods: {
    async getAttendedPresentationDetail() {
      try {
        const attendedPresentationId =
          this.$route.query['attended-presentation-detail']
        const attendedPresentationDetail =
          await AttendedPresentationsApi.getAttendedPresentationDetail(
            attendedPresentationId,
          )

        if (attendedPresentationDetail) {
          this.detail = this._transformData(attendedPresentationDetail)
        }
      } catch (error) {
        Sentry.captureException(error)
        // force to back to the list
        this.$router.replace({
          query: {},
        })
        util.showToastError(error.message)
      } finally {
        this.isLoading = false
      }
    },
    _transformData(data = {}) {
      return {
        id: data.id,
        userId: data.userId,
        audienceId: data.audienceId,
        joinAt: data.joinAt,
        presentationId: data.presentationId,
        name: data.presentation && data.presentation.name,
        thumbnailImage: data.presentation && data.presentation.thumbnailImage,
        customThumbnailImage:
          data.presentation && data.presentation.customThumbnailImage,
        presentation: data.presentation,
      }
    },
    backToAttendedPresentationList() {
      this.$router.replace({
        query: {},
      })

      util.ga4Track('Account > Presentation details > Back', {
        presentationId: this.detail && this.detail.presentationId,
      })
    },
    async getDataSlides(presentationId) {
      try {
        const dataSlides = await SlideApi.getSlides(presentationId)

        if (dataSlides && dataSlides.length) {
          this.slides = dataSlides
        }
      } catch (e) {
        Sentry.captureException(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.presentation-detail {
  &-header {
    position: relative;
    background-color: #fff;
    border-bottom: #eff1f7;
  }

  .back-to-list {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 600px;
    text-align: center;
    position: relative;
    padding: 16px;
    cursor: pointer;

    .text {
      color: #191f2d;
      font-weight: bold;
      text-transform: capitalize;
    }

    .icon {
      color: #0a0a0ab2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      position: absolute;
      top: 50%;
      left: 16px;
      transform: translateY(-50%);
    }

    .text-support {
      display: none;
    }
  }
}

.presentation-detail-loading {
  max-width: 558px;
  margin: 60px auto 0 auto;
  padding: 0 16px;
}

@media screen and (min-width: 1280px) {
  .presentation-detail {
    .back-to-list {
      max-width: 100%;
      padding: 16px 40px;

      .icon {
        left: 40px;
      }

      .text {
        font-size: 1.25rem;
      }

      .text-support {
        display: inline-block;
        position: absolute;
        top: 50%;
        left: 68px;
        transform: translateY(-50%);
        color: #191f2d;
        font-weight: bold;
      }
    }
  }
}
</style>
